'use client';

import { useState, useRef, useCallback, useEffect, forwardRef, type MouseEvent as ReactMouseEvent } from 'react';
import { cn } from '@/core/utils/cn';
import { ChevronDown, Check, Search } from 'lucide-react';
import { useTranslation } from '@/core/i18n';

interface SelectOption {
  label: string;
  value: string;
}

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  options: SelectOption[];
  placeholder?: string;
  /** Optional i18n key map: maps English label strings to translation keys.
   *  When provided, labels are translated via useTranslation().t(). */
  i18nKeys?: Record<string, string>;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, label, error, options, placeholder, id, value, onChange, disabled, i18nKeys, ...rest }, ref) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);
    const nativeRef = useRef<HTMLSelectElement | null>(null);
    const selectId = id || label?.toLowerCase().replace(/\s+/g, '-');
    const { t } = useTranslation();

    /** Translate option label using i18nKeys map when provided */
    const tLabel = useCallback((lbl: string) => {
      if (!i18nKeys) return lbl;
      const key = i18nKeys[lbl];
      return key ? t(key) : lbl;
    }, [i18nKeys, t]);

    const selectedOpt = options.find(o => o.value === String(value ?? ''));
    const displayLabel = selectedOpt ? tLabel(selectedOpt.label) : '';
    const showSearch = options.length > 6;

    const filtered = search
      ? options.filter(o => tLabel(o.label).toLowerCase().includes(search.toLowerCase()))
      : options;

    /* Close on outside click / Escape */
    useEffect(() => {
      if (!open) return;
      const handleOutside = (e: globalThis.MouseEvent) => {
        if (!containerRef.current?.contains(e.target as Node)) {
          setOpen(false);
          setSearch('');
        }
      };
      const handleKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape') { setOpen(false); setSearch(''); }
      };
      document.addEventListener('mousedown', handleOutside);
      document.addEventListener('keydown', handleKey);
      return () => {
        document.removeEventListener('mousedown', handleOutside);
        document.removeEventListener('keydown', handleKey);
      };
    }, [open]);

    /* Select option from custom dropdown */
    const handleOptionClick = useCallback((optValue: string) => {
      setOpen(false);
      setSearch('');
      if (nativeRef.current && onChange) {
        nativeRef.current.value = optValue;
        onChange({
          target: nativeRef.current,
          currentTarget: nativeRef.current,
        } as React.ChangeEvent<HTMLSelectElement>);
      }
    }, [onChange]);

    /* Prevent native dropdown, toggle custom */
    const handleNativeMouseDown = useCallback((e: ReactMouseEvent<HTMLSelectElement>) => {
      e.preventDefault();
      if (!disabled) { setOpen(prev => !prev); setSearch(''); }
    }, [disabled]);

    /* Ref merge for forwarding + internal access */
    const setRef = useCallback((el: HTMLSelectElement | null) => {
      nativeRef.current = el;
      if (typeof ref === 'function') ref(el);
      else if (ref) (ref as React.MutableRefObject<HTMLSelectElement | null>).current = el;
    }, [ref]);

    return (
      <div ref={containerRef} className={cn('space-y-1.5', className)}>
        {label && (
          <label htmlFor={selectId} className="block text-[12px] font-medium text-hms-textSub">
            {label}
          </label>
        )}
        <div className="relative">
          {/* Visual trigger — pointer-events-none so clicks pass to native select */}
          <div
            className={cn(
              'w-full h-9 flex items-center justify-between px-3 rounded-lg border transition-all',
              'bg-hms-bg text-[13px] pointer-events-none select-none',
              open ? 'border-hms-gold/50 ring-2 ring-hms-gold/30' :
              error ? 'border-hms-red/50' :
              'border-hms-border',
              disabled && 'opacity-50',
            )}
          >
            <span className={cn('truncate', value ? 'text-hms-text' : 'text-hms-textDim')}>
              {displayLabel || placeholder || 'Select...'}
            </span>
            <ChevronDown className={cn(
              'w-3.5 h-3.5 text-hms-textDim shrink-0 ml-2 transition-transform duration-200',
              open && 'rotate-180',
            )} />
          </div>

          {/* Native select — transparent overlay for Playwright E2E compat */}
          <select
            ref={setRef}
            id={selectId}
            value={value}
            onChange={onChange}
            disabled={disabled}
            onMouseDown={handleNativeMouseDown}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            aria-label={label}
            {...rest}
          >
            {placeholder && <option value="">{placeholder}</option>}
            {options.map(opt => (
              <option key={opt.value} value={opt.value}>{tLabel(opt.label)}</option>
            ))}
          </select>

          {/* Custom dropdown panel */}
          {open && (
            <div
              id={selectId ? `${selectId}-dropdown` : undefined}
              className="absolute z-50 mt-1 w-full rounded-lg border border-hms-border bg-hms-card shadow-xl shadow-black/30 overflow-hidden"
              role="listbox"
            >
              {showSearch && (
                <div className="flex items-center gap-2 px-3 py-2 border-b border-hms-border/50">
                  <Search className="w-3.5 h-3.5 text-hms-textDim shrink-0" />
                  <input
                    id={selectId ? `${selectId}-search` : undefined}
                    type="text"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder={t('shared.select.search')}
                    className="flex-1 bg-transparent text-[12px] text-hms-text placeholder:text-hms-textDim outline-none"
                    autoFocus
                  />
                </div>
              )}
              <div className="max-h-[200px] overflow-y-auto py-1 scrollbar-thin">
                {filtered.length === 0 ? (
                  <p className="text-[11px] text-hms-textDim text-center py-3">No options found</p>
                ) : (
                  filtered.map(opt => {
                    const selected = String(opt.value) === String(value ?? '');
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        id={selectId ? `${selectId}-opt-${opt.value}` : undefined}
                        role="option"
                        aria-selected={selected}
                        onClick={() => handleOptionClick(opt.value)}
                        className={cn(
                          'w-full flex items-center justify-between px-3 py-2 text-left text-[12px] transition-colors',
                          selected
                            ? 'bg-hms-gold/[0.08] text-hms-gold font-semibold'
                            : 'text-hms-textSub hover:bg-hms-surface hover:text-hms-text',
                        )}
                      >
                        <span>{tLabel(opt.label)}</span>
                        {selected && <Check className="w-3.5 h-3.5 text-hms-gold shrink-0" />}
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>
        {error && <p className="text-[11px] text-hms-red">{error}</p>}
      </div>
    );
  },
);
Select.displayName = 'Select';
